"use client";

import { useEffect, useRef } from "react";

const termGroups = [
    {
        group: "Agreement Structure",
        terms: [
            { label: "Contract Duration", value: "1, 2, 3, or 5 years (renewable)" },
            { label: "Governing Law", value: "English Law — LCIA Arbitration" },
            { label: "Minimum Volume", value: "500 metric tonnes per annum" },
            { label: "Maximum Volume", value: "5,000 metric tonnes per annum" },
            { label: "Volume Flexibility", value: "±10% per quarterly tranche" },
        ],
    },
    {
        group: "Pricing & Payment",
        terms: [
            { label: "Pricing Basis", value: "LME Antimony Metal Monthly Average" },
            { label: "Premium / Discount", value: "Agreed at contract inception; reviewed annually" },
            { label: "Payment Terms", value: "100% by irrevocable L/C at sight, or T/T 30 days with approved credit" },
            { label: "Currency", value: "USD (default); EUR available" },
            { label: "Invoice Date", value: "D+3 from Bill of Lading date" },
        ],
    },
    {
        group: "Quality & Delivery",
        terms: [
            { label: "Grade", value: "Sb ≥99.50% (standard); ≥99.85% (high-purity)" },
            { label: "Packaging", value: "25 kg PP drums or 1 MT big bags" },
            { label: "Incoterms", value: "CIF, CFR, or DAP — buyer's election" },
            { label: "Lead Time", value: "45–60 days from order confirmation" },
            { label: "Inspection", value: "Joint PSI; SGS / BV / Intertek" },
        ],
    },
    {
        group: "Termination & Force Majeure",
        terms: [
            { label: "Termination for Cause", value: "30-day cure period; then immediate" },
            { label: "Termination for Convenience", value: "90-day written notice; volume make-whole applies" },
            { label: "Force Majeure", value: "Standard ISDA-style; 60-day trigger" },
            { label: "Change of Control", value: "Consent required; not to be unreasonably withheld" },
        ],
    },
];

export default function OfftakeTermsSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "100px 0" }}
            aria-labelledby="offtake-terms"
            id="offtake-terms"
        >
            <div
                className="px-6 lg:px-12 items-center"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
            <div
                className="px-6 lg:px-12 text-center"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                <div
                    className="flex items-center justify-center gap-3 mb-8 reveal"
                >
                    <span className="type-subheading">Offtake Terms</span>
                </div>
                <h1
                    className="reveal reveal-delay-1"
                    style={{
                        fontFamily: "var(--font-serif)",
                        fontSize: "clamp(2.5rem, 5vw, 4rem)",
                        fontWeight: 400,
                        color: "var(--color-white)",
                        lineHeight: 1.05,
                        letterSpacing: "-0.02em",
                        margin: "0 auto",
                    }}
                >
                        Standard commercial
                        terms at a glance.
                </h1>
                <p
                    className="reveal reveal-delay-2"
                    style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "1.0625rem",
                        color: "rgba(255,255,255,0.55)",
                        lineHeight: "1.7",
                        marginTop: "20px",
                        margin: "20px auto 0",
                    }}
                >
                        All terms are negotiable within the parameters below. Long-form
                        agreements are available on request under mutual NDA.
                </p>
            </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                    {termGroups?.map((group, gi) => (
                        <div
                            key={group?.group}
                            className={`reveal reveal-delay-${gi + 1}`}
                            style={{
                                background: "var(--color-navy-mid)",
                                border: "1px solid var(--color-border)",
                                borderRadius: "4px",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    background: "var(--color-navy)",
                                    padding: "16px 24px",
                                }}
                            >
                                <p
                                    style={{
                                        fontFamily: "var(--font-sans)",
                                        fontSize: "0.6875rem",
                                        fontWeight: 600,
                                        letterSpacing: "0.12em",
                                        textTransform: "uppercase",
                                        color: "var(--color-red)",
                                    }}
                                >
                                    {group?.group}
                                </p>
                            </div>
                            <table
                                className="data-table"
                                aria-label={`${group?.group} terms`}
                            >
                                <tbody>
                                    {group?.terms?.map((term) => (
                                        <tr key={term?.label}>
                                            <td
                                                style={{
                                                    fontWeight: 500,
                                                    color: "var(--color-white)",
                                                    fontSize: "0.8125rem",
                                                    width: "42%",
                                                }}
                                            >
                                                {term?.label}
                                            </td>
                                            <td
                                                style={{
                                                    color: "rgba(255, 255, 255, 0.75)",
                                                    fontSize: "0.875rem",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {term?.value}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>



        </section>
    );
}